<script setup>
import { getProjectId } from '@/utils/cdms'
import { useSnackBar } from '@/utils/stores/snackbar'
import { VDataTable } from 'vuetify/labs/VDataTable'

definePage({
  meta: {
    action: 'blind',
    subject: 'rtms',
  },
})

const snackbar = useSnackBar()
const projectId = getProjectId()

const formatDate = (v) => v ? String(v).slice(0, 10) : '-'

// 소수점 포맷팅
const decimalType = ref('integer')
const formatQty = (val) => {
  if (val == null || val === '') return '-'
  const num = Number(val)
  if (isNaN(num)) return val
  if (decimalType.value === 'decimal1') return num.toFixed(1)
  if (decimalType.value === 'decimal2') return num.toFixed(2)
  return String(Math.floor(num))
}

// decimalType에 따라 소수점 입력 제한
const onDecimalInput = (e) => {
  const maxDecimals = decimalType.value === 'integer' ? 0 : decimalType.value === 'decimal1' ? 1 : 2
  const allowed = maxDecimals > 0 ? /[0-9.]/ : /[0-9]/
  if (!allowed.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
    e.preventDefault()
    return
  }
  const target = e.target
  const val = target.value
  if (maxDecimals === 0 && e.key === '.') {
    e.preventDefault()
    return
  }
  if (e.key === '.' && val.includes('.')) {
    e.preventDefault()
    return
  }
  const dotIdx = val.indexOf('.')
  if (dotIdx !== -1 && target.selectionStart > dotIdx && val.length - dotIdx > maxDecimals && !['Backspace', 'Delete'].includes(e.key)) {
    e.preventDefault()
  }
}

// ─────────────────────────────────────────
// 사이트 목록 로드 (드롭다운용)
// ─────────────────────────────────────────
const siteOptions = ref([])

const loadSites = async () => {
  try {
    const { data, error } = await useApi(createUrl('/sources/sites/all', {
      query: { projectId, type: 'investigator' },
    }))
    if (!error.value && data.value) {
      siteOptions.value = data.value.map(s => ({
        value: s.site_id ?? s.id,
        title: `${s.site_no ?? s.site_id ?? s.id} - ${s.name ?? s.site_name ?? ''}`,
        site_no: s.site_no ?? s.site_id ?? s.id,
        site_name: s.name ?? s.site_name ?? '',
      })).sort((a, b) => {
        const noA = Number(a.site_no) || 0
        const noB = Number(b.site_no) || 0
        return noA - noB
      })
    }
  } catch (e) {
    siteOptions.value = []
  }
}

// ─────────────────────────────────────────
// 배송 요청 표 (상단)
// ─────────────────────────────────────────
const requestRows = ref([])
const requestLoading = ref(false)

const requestHeaders = [
  { title: 'No.',          key: 'no',              width: '60px',  align: 'center' },
  { title: '요청 구분',    key: 'request_type',    width: '130px', align: 'center' },
  { title: '요청 사유',    key: 'reason_category', width: '120px' },
  { title: '기관번호',     key: 'site_no',         width: '100px', align: 'center' },
  { title: '기관명',       key: 'site_name',       width: '160px' },
  { title: '생성일',       key: 'created_at',      width: '150px', align: 'center' },
  { title: '희망도착일',   key: 'desired_date',    width: '110px', align: 'center' },
  { title: '요청량',       key: 'requested_quantity', width: '90px', align: 'center' },
  { title: '현재 재고량',  key: 'remaining_quantity', width: '100px', align: 'center' },
  { title: '기타 요청사항', key: 'reason_detail',  width: '200px' },
  { title: '관리',         key: 'actions',         width: '160px', align: 'center', sortable: false },
]

const filterRequestType = ref('')
const filterReason = ref('')
const filterDateFrom = ref('')
const filterDateTo = ref('')
const requestPage = ref(1)
const requestItemsPerPage = ref(10)

const requestTypeOptions = [
  { value: '', title: '전체' },
  { value: 'AUTO', title: '자동' },
  { value: 'MANUAL', title: '수동' },
]

const reasonFilterOptions = [
  { value: '', title: '전체' },
  { value: '긴급 등록 증가', title: '긴급 등록 증가' },
  { value: '파손·분실', title: '파손·분실' },
  { value: '기타', title: '기타' },
]

const loadRequests = async () => {
  requestLoading.value = true
  try {
    const query = { projectId }
    if (filterRequestType.value) query.requestType = filterRequestType.value
    if (filterReason.value) query.reasonCategory = filterReason.value
    if (filterDateFrom.value) query.dateFrom = filterDateFrom.value
    if (filterDateTo.value) query.dateTo = filterDateTo.value

    const { data, error } = await useApi(createUrl('/drug-management/supply-requests', { query }))
    if (!error.value && data.value) {
      if (data.value.decimalType) decimalType.value = data.value.decimalType
      const items = data.value.items ?? data.value
      requestRows.value = items.map((r, idx) => ({
        ...r,
        no: idx + 1,
        request_type_label: r.request_type === 'AUTO' ? '자동배송요청' : '수동배송요청',
      }))
    }
  } catch (e) {
    console.error('배송 요청 로드 실패:', e)
  } finally {
    requestLoading.value = false
  }
}

const searchRequests = () => {
  requestPage.value = 1
  loadRequests()
}

const resetRequestFilter = () => {
  filterRequestType.value = ''
  filterReason.value = ''
  filterDateFrom.value = ''
  filterDateTo.value = ''
  loadRequests()
}

// ─────────────────────────────────────────
// 배송 요청 삭제 (거절)
// ─────────────────────────────────────────
const deleteRequest = async (item) => {
  if (!confirm('이 배송 요청을 삭제(거절)하시겠습니까?')) return

  try {
    await $api(`/drug-management/supply-requests/${item.id}`, { method: 'DELETE' })
    snackbar.showSnackBar('배송 요청이 거절 처리되었습니다.', 'success')
    await Promise.all([loadRequests(), loadDeliveries()])
  } catch (e) {
    snackbar.showSnackBar('배송 요청 삭제에 실패했습니다.', 'error')
  }
}

// ─────────────────────────────────────────
// 배송 처리 다이얼로그
// ─────────────────────────────────────────
const isDeliveryDialogOpen = ref(false)
const deliveryTarget = ref(null)
const deliveryForm = reactive({
  expected_arrival: '',
  memo: '',
})
const selectedDrugs = ref([])
const searchingDrugs = ref(false)

const openDeliveryDialog = async (item) => {
  deliveryTarget.value = item
  deliveryForm.expected_arrival = ''
  deliveryForm.memo = ''
  selectedDrugs.value = []
  isDeliveryDialogOpen.value = true

  // 자동요청이면 해당 군의 Drug No. 1개 자동 로드
  if (item.request_type === 'AUTO' && item.group_type) {
    await fetchDeliveryDrugs({ limit: 1 })
  }
}

const fetchDeliveryDrugs = async (options = {}) => {
  if (!deliveryTarget.value) return
  searchingDrugs.value = true
  try {
    const excludeIds = selectedDrugs.value.map(d => d.id)

    // 배송표에 이미 Drug No.가 있으면 첫 번째 항목의 group_type 기준
    const firstDrug = selectedDrugs.value[0]
    const groupType = firstDrug?.group_type || deliveryTarget.value.group_type || ''

    const query = {
      projectId,
      siteId: deliveryTarget.value.site_id,
      supplyRequestId: deliveryTarget.value.id,
      excludeIds,
    }
    if (groupType) query.groupType = groupType
    if (options.limit) query.limit = options.limit

    const { data, error } = await useApi(createUrl('/drug-management/delivery/search-drugs', { query }))
    if (!error.value && data.value) {
      const existingIds = new Set(selectedDrugs.value.map(d => d.id))
      const newDrugs = data.value
        .filter(d => !existingIds.has(d.id))
        .map(d => ({
          id: d.id,
          drug_no: d.drug_no,
          group_type: d.management_number?.group_type || '',
          current_stock: d.current_stock ?? d.quantity ?? 0,
          quantity: 1,
        }))

      selectedDrugs.value = [...selectedDrugs.value, ...newDrugs]
    }
    if (selectedDrugs.value.length === 0 && !options.limit) {
      snackbar.showSnackBar('배송 가능한 Drug No.가 없습니다.', 'warning')
    }
  } catch (e) {
    if (!options.limit) {
      snackbar.showSnackBar('Drug No. 검색에 실패했습니다.', 'error')
    }
  } finally {
    searchingDrugs.value = false
  }
}

const searchDeliveryDrugs = () => fetchDeliveryDrugs()

const removeDrug = (idx) => {
  selectedDrugs.value.splice(idx, 1)
}

const totalSelected = computed(() =>
  selectedDrugs.value.reduce((sum, d) => sum + (Number(d.quantity) || 0), 0),
)

const saveDelivery = async () => {
  if (!deliveryForm.expected_arrival) {
    snackbar.showSnackBar('배송 완료 예정일을 입력해주세요.', 'error')
    return
  }
  if (selectedDrugs.value.length === 0) {
    snackbar.showSnackBar('전달할 Drug No.를 선택해주세요.', 'error')
    return
  }

  try {
    await $api('/drug-management/delivery', {
      method: 'POST',
      body: {
        projectId,
        supplyRequestId: deliveryTarget.value.id,
        siteId: deliveryTarget.value.site_id,
        drugs: selectedDrugs.value.map(d => ({ drugId: d.id, quantity: Number(d.quantity) || 1 })),
        deliveryDueDate: deliveryForm.expected_arrival,
        memo: deliveryForm.memo,
      },
    })
    snackbar.showSnackBar('배송 처리가 완료되었습니다.', 'success')
    isDeliveryDialogOpen.value = false
    await Promise.all([loadRequests(), loadDeliveries()])
  } catch (e) {
    snackbar.showSnackBar('배송 처리에 실패했습니다.', 'error')
  }
}

// ─────────────────────────────────────────
// 일반 배송 처리 팝업 (배송 추가 / 수동배송 처리)
// ─────────────────────────────────────────
const isGeneralDeliveryOpen = ref(false)
const generalDeliveryTarget = ref(null) // 수동배송요청 처리 시 해당 요청 객체
const generalForm = reactive({
  site: '',
  expected_arrival: '',
})
const generalDrugs = ref([])
const generalDrugSearch = ref('')
const generalSearching = ref(false)

const openGeneralDeliveryDialog = (item = null) => {
  generalDeliveryTarget.value = item
  generalDrugs.value = []
  generalDrugSearch.value = ''
  if (item) {
    // 수동배송요청의 배송처리 → Site 정보 프리필
    generalForm.site = item.site_id
    generalForm.expected_arrival = ''
  } else {
    // 배송 추가 (최초 배송)
    generalForm.site = ''
    generalForm.expected_arrival = ''
  }
  isGeneralDeliveryOpen.value = true
}

const searchDrugByNo = async () => {
  const drugNo = generalDrugSearch.value.trim()
  if (!drugNo) {
    snackbar.showSnackBar('Drug No.를 입력해주세요.', 'error')
    return
  }

  // 이미 추가된 Drug No.인지 확인
  if (generalDrugs.value.some(d => d.drug_no === drugNo)) {
    snackbar.showSnackBar('이미 추가된 Drug No.입니다.', 'warning')
    return
  }

  generalSearching.value = true
  try {
    const { data, error } = await useApi(createUrl('/drug-management/delivery/search-drug-by-no', {
      query: { projectId, drugNo },
    }))

    if (error.value) {
      snackbar.showSnackBar('검색에 실패했습니다.', 'error')
      return
    }

    const code = data.value?.code || ''
    if (code === 'SUCCESS') {
      const drug = data.value.drug
      generalDrugs.value.push({
        id: drug.id,
        drug_no: drug.drug_no,
        current_stock: data.value.current_stock,
        quantity: 1,
      })
      generalDrugSearch.value = ''
    } else if (code === 'NOT_FOUND') {
      snackbar.showSnackBar('존재하지 않는 Drug No.입니다.', 'error')
    } else if (code === 'NO_STOCK') {
      snackbar.showSnackBar(data.value?.message || '해당 Drug No.는 재고에 없습니다.', 'warning')
    } else {
      snackbar.showSnackBar('검색에 실패했습니다.', 'error')
    }
  } catch (e) {
    snackbar.showSnackBar('검색에 실패했습니다.', 'error')
  } finally {
    generalSearching.value = false
  }
}

const removeGeneralDrug = (idx) => {
  generalDrugs.value.splice(idx, 1)
}

const qtyStep = computed(() => {
  if (decimalType.value === 'decimal1') return 0.5
  if (decimalType.value === 'decimal2') return 0.25
  return 1
})

const adjustQty = (idx, direction) => {
  const drug = generalDrugs.value[idx]
  const decimals = decimalType.value === 'integer' ? 0 : decimalType.value === 'decimal1' ? 1 : 2
  const minVal = decimals === 0 ? 1 : parseFloat((0.1 ** decimals).toFixed(decimals))
  const delta = direction * qtyStep.value
  const next = Math.max(minVal, parseFloat(((Number(drug.quantity) || 0) + delta).toFixed(decimals)))
  drug.quantity = Math.min(next, drug.current_stock || 1)
}

const onDirectQtyInput = (idx, val) => {
  const drug = generalDrugs.value[idx]
  const decimals = decimalType.value === 'integer' ? 0 : decimalType.value === 'decimal1' ? 1 : 2
  const minVal = decimals === 0 ? 1 : parseFloat((0.1 ** decimals).toFixed(decimals))
  let num = parseFloat(val)
  if (isNaN(num) || num < minVal) num = minVal
  num = Math.min(num, drug.current_stock || 1)
  drug.quantity = parseFloat(num.toFixed(decimals))
}

const generalTotalQty = computed(() =>
  generalDrugs.value.reduce((sum, d) => sum + (Number(d.quantity) || 0), 0),
)

const saveGeneralDelivery = async () => {
  if (!generalForm.site) {
    snackbar.showSnackBar('배송 대상 Site를 선택해주세요.', 'error')
    return
  }
  if (!generalForm.expected_arrival) {
    snackbar.showSnackBar('도착 예정일을 입력해주세요.', 'error')
    return
  }
  if (generalDrugs.value.length === 0) {
    snackbar.showSnackBar('배송할 Drug No.를 추가해주세요.', 'error')
    return
  }

  try {
    await $api('/drug-management/delivery', {
      method: 'POST',
      body: {
        projectId,
        supplyRequestId: generalDeliveryTarget.value?.id || null,
        siteId: generalForm.site,
        drugs: generalDrugs.value.map(d => ({ drugId: d.id, quantity: Number(d.quantity) || 1 })),
        deliveryDueDate: generalForm.expected_arrival,
        memo: '',
      },
    })
    snackbar.showSnackBar('배송 처리가 완료되었습니다.', 'success')
    isGeneralDeliveryOpen.value = false
    await Promise.all([loadRequests(), loadDeliveries()])
  } catch (e) {
    snackbar.showSnackBar('배송 처리에 실패했습니다.', 'error')
  }
}

const onGeneralDeliveryOutsideClick = () => {
  if (clickStartedOnFlatpickr) { clickStartedOnFlatpickr = false; return }
  if (document.querySelector('.flatpickr-calendar.open')) return
  isGeneralDeliveryOpen.value = false
}

// ─────────────────────────────────────────
// 배송 처리 내역 (하단)
// ─────────────────────────────────────────
const deliveryRows = ref([])
const deliveryLoading = ref(false)

const deliveryHeaders = [
  { title: 'No.',             key: 'no',               width: '60px',  align: 'center' },
  { title: '배송처리일',      key: 'processed_at',     width: '150px', align: 'center' },
  { title: '배송완료 예정일', key: 'delivery_due_date', width: '140px', align: 'center' },
  { title: '기관번호',        key: 'site_no',          width: '90px',  align: 'center' },
  { title: '기관명',          key: 'site_name',        width: '160px' },
  { title: 'Drug No.',        key: 'drug_no',          width: '130px' },
  { title: '배송 수량',       key: 'quantity',         width: '110px', align: 'center' },
  { title: '작업자',          key: 'processed_by_name', width: '110px', align: 'center' },
  { title: '비고',            key: 'note',             width: '130px', align: 'center' },
]

const deliveryPage = ref(1)
const deliveryItemsPerPage = ref(10)

const loadDeliveries = async () => {
  deliveryLoading.value = true
  try {
    const { data, error } = await useApi(createUrl('/drug-management/deliveries', {
      query: { projectId },
    }))
    if (!error.value && data.value) {
      deliveryRows.value = data.value.map((d, idx) => ({
        ...d,
        no: idx + 1,
      }))
    }
  } catch (e) {
    console.error('배송 내역 로드 실패:', e)
  } finally {
    deliveryLoading.value = false
  }
}

// 배송 완료예정일 수정 다이얼로그
const isEditArrivalOpen = ref(false)
const editArrivalTarget = ref(null)
const editArrivalDate = ref('')

const openEditArrival = (item) => {
  editArrivalTarget.value = item
  editArrivalDate.value = item.delivery_due_date
  isEditArrivalOpen.value = true
}

const saveEditArrival = async () => {
  if (!editArrivalDate.value) {
    snackbar.showSnackBar('예정일을 입력해주세요.', 'error')
    return
  }

  try {
    await $api(`/drug-management/deliveries/${editArrivalTarget.value.id}`, {
      method: 'PATCH',
      body: { deliveryDueDate: editArrivalDate.value },
    })
    snackbar.showSnackBar('배송 완료예정일이 수정되었습니다.', 'success')
    isEditArrivalOpen.value = false
    await loadDeliveries()
  } catch (e) {
    snackbar.showSnackBar('예정일 수정에 실패했습니다.', 'error')
  }
}

// ─────────────────────────────────────────
// 엑셀 다운로드
// ─────────────────────────────────────────
const downloadDeliveryExcel = async () => {
  try {
    await $api('/drug-management/export', {
      method: 'POST',
      body: { projectId, exportType: 'deliveries' },
    })
    snackbar.showSnackBar('요청이 완료되었습니다. 결과는 이메일로 전달됩니다.', 'success')
  } catch (e) {
    snackbar.showSnackBar('엑셀 다운로드 요청에 실패했습니다.', 'error')
  }
}

// ─────────────────────────────────────────
// 초기 로드
// ─────────────────────────────────────────
onMounted(async () => {
  await loadSites()
  await Promise.all([loadRequests(), loadDeliveries()])
})

// ─────────────────────────────────────────
// 다이얼로그 닫기: persistent 유지 + 수동 ESC/바깥클릭 처리
// ─────────────────────────────────────────
let clickStartedOnFlatpickr = false

const onDocPointerDown = (e) => {
  clickStartedOnFlatpickr = !!e.target.closest('.flatpickr-calendar')
}

const onDocKeyDown = (e) => {
  if (e.key !== 'Escape') return
  if (document.querySelector('.flatpickr-calendar.open')) return
  if (isEditArrivalOpen.value) { isEditArrivalOpen.value = false; return }
  if (isDeliveryDialogOpen.value) { isDeliveryDialogOpen.value = false; return }
  if (isGeneralDeliveryOpen.value) { isGeneralDeliveryOpen.value = false; return }
}

const onDeliveryOutsideClick = () => {
  if (clickStartedOnFlatpickr) { clickStartedOnFlatpickr = false; return }
  if (document.querySelector('.flatpickr-calendar.open')) return
  isDeliveryDialogOpen.value = false
}

const onAddOutsideClick = () => {
  if (clickStartedOnFlatpickr) { clickStartedOnFlatpickr = false; return }
  if (document.querySelector('.flatpickr-calendar.open')) return
  isGeneralDeliveryOpen.value = false
}

const onEditArrivalOutsideClick = () => {
  if (clickStartedOnFlatpickr) { clickStartedOnFlatpickr = false; return }
  if (document.querySelector('.flatpickr-calendar.open')) return
  isEditArrivalOpen.value = false
}

onMounted(() => {
  document.addEventListener('pointerdown', onDocPointerDown, true)
  document.addEventListener('keydown', onDocKeyDown, true)
})

onUnmounted(() => {
  document.removeEventListener('pointerdown', onDocPointerDown, true)
  document.removeEventListener('keydown', onDocKeyDown, true)
})
</script>

<template>
  <div>
    <div class="mb-1">
      <h5 class="text-h5 font-weight-bold">배송 요청 관리</h5>
    </div>

    <!-- ══════════════════════════════════════════════ -->
    <!-- 배송 요청 표                                    -->
    <!-- ══════════════════════════════════════════════ -->
    <VCard class="mt-4 mb-6">
      <VCardText>
        <div class="d-flex justify-space-between align-center mb-4">
          <span class="text-subtitle-1 font-weight-bold">배송 요청 표</span>
          <VBtn variant="outlined" size="small" @click="loadRequests">
            새로고침
          </VBtn>
        </div>

        <!-- 필터 -->
        <VRow class="mb-3" align="end">
          <VCol cols="12" md="2">
            <AppSelect
              v-model="filterRequestType"
              :items="requestTypeOptions"
              label="요청 구분"
              placeholder="전체"
              hide-details
            />
          </VCol>
          <VCol cols="12" md="2">
            <AppSelect
              v-model="filterReason"
              :items="reasonFilterOptions"
              label="요청 사유"
              placeholder="전체"
              hide-details
            />
          </VCol>
          <VCol cols="12" md="2">
            <AppDateTimePicker
              v-model="filterDateFrom"
              label="생성일"
              placeholder="시작일"
              hide-details
            />
          </VCol>
          <VCol cols="12" md="2">
            <AppDateTimePicker
              v-model="filterDateTo"
              label="생성일 (종료)"
              placeholder="종료일"
              hide-details
            />
          </VCol>
          <VCol cols="12" md="4" class="d-flex gap-2 align-center">
            <VBtn color="primary" @click="searchRequests">
              <VIcon icon="tabler-search" size="16" class="me-1" />검색
            </VBtn>
            <VBtn variant="outlined" @click="resetRequestFilter">
              초기화
            </VBtn>
            <VSpacer />
            <VBtn color="primary" @click="openGeneralDeliveryDialog()">
              배송 추가
            </VBtn>
          </VCol>
        </VRow>

        <VDataTable
          v-model:items-per-page="requestItemsPerPage"
          v-model:page="requestPage"
          :items="requestRows"
          :headers="requestHeaders"
          :loading="requestLoading"
          density="compact"
          class="rtms-table"
        >
          <template #item.request_type="{ item }">
            <VChip
              :color="item.request_type === 'AUTO' ? 'info' : 'warning'"
              size="small"
              label
            >
              {{ item.request_type_label }}
            </VChip>
          </template>

          <template #item.requested_quantity="{ item }">
            <template v-if="item.request_type === 'AUTO'">
              <span class="text-medium-emphasis">-</span>
            </template>
            <template v-else>{{ formatQty(item.requested_quantity) }}</template>
          </template>

          <template #item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template #item.desired_date="{ item }">
            {{ formatDate(item.desired_date) }}
          </template>

          <template #item.remaining_quantity="{ item }">
            <template v-if="item.remaining_quantity === null">
              <span class="text-medium-emphasis">-</span>
            </template>
            <VChip
              v-else
              :color="item.remaining_quantity > 0 ? 'warning' : 'success'"
              size="x-small"
              label
            >
              {{ formatQty(item.remaining_quantity) }}
            </VChip>
          </template>

          <template #item.reason_detail="{ item }">
            <span class="text-truncate d-inline-block" style="max-width: 190px">
              {{ item.reason_detail || '-' }}
            </span>
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex gap-1 justify-center">
              <VBtn
                variant="outlined"
                color="primary"
                size="x-small"
                @click="item.request_type === 'AUTO' ? openDeliveryDialog(item) : openGeneralDeliveryDialog(item)"
              >
                배송처리
              </VBtn>
              <VBtn variant="outlined" color="error" size="x-small" @click="deleteRequest(item)">
                배송 요청 삭제
              </VBtn>
            </div>
          </template>

          <template #bottom="{ pageCount }">
            <div class="pa-3">
              <VPagination
                v-model="requestPage"
                :length="pageCount"
                size="small"
                rounded
              />
              <p class="text-caption text-medium-emphasis mt-2 mb-0">
                ⓘ 자동배송요청: 각 Site의 재고가 설정된 최소 재고 이하로 감소할 경우 자동으로 생성됩니다.<br>
                ⓘ 수동배송요청: 관리자가 직접 요청을 등록한 경우입니다.
              </p>
            </div>
          </template>
        </VDataTable>
      </VCardText>
    </VCard>

    <!-- ══════════════════════════════════════════════ -->
    <!-- 배송 처리 내역 (하단)                            -->
    <!-- ══════════════════════════════════════════════ -->
    <VCard>
      <VCardText>
        <div class="d-flex justify-space-between align-center mb-1">
          <span class="text-subtitle-1 font-weight-bold">배송 처리 내역</span>
          <div class="d-flex gap-2">
            <VBtn variant="outlined" size="small" prepend-icon="tabler-file-spreadsheet" @click="downloadDeliveryExcel">
              엑셀 다운로드
            </VBtn>
            <VBtn variant="outlined" size="small" prepend-icon="tabler-adjustments-horizontal">
              조건별 필터
            </VBtn>
          </div>
        </div>
        <p class="text-caption text-medium-emphasis mb-4">
          ⓘ 배송완료예정일에 각 Site의 Inventory에 자동 반영되어 Available 상태가 됩니다.
        </p>

        <VDataTable
          v-model:items-per-page="deliveryItemsPerPage"
          v-model:page="deliveryPage"
          :items="deliveryRows"
          :headers="deliveryHeaders"
          :loading="deliveryLoading"
          density="compact"
          class="rtms-table"
        >
          <template #item.processed_at="{ item }">
            {{ formatDate(item.processed_at) }}
          </template>

          <template #item.delivery_due_date="{ item }">
            <div class="d-inline-flex align-center position-relative">
              <span>{{ formatDate(item.delivery_due_date) }}</span>
              <VBtn icon variant="text" size="x-small" color="primary" class="edit-arrival-btn" @click="openEditArrival(item)">
                <VIcon icon="tabler-pencil" size="13" />
              </VBtn>
            </div>
          </template>

          <template #item.quantity="{ item }">
            {{ formatQty(item.quantity) }}
          </template>

          <template #item.note="{ item }">
            <VChip
              v-if="item.status === 'REJECTED'"
              color="error"
              size="x-small"
              label
            >
              거절
            </VChip>
            <VChip
              v-else-if="item.status === 'DELIVERED'"
              color="success"
              size="x-small"
              label
            >
              배송완료
            </VChip>
            <VChip
              v-else
              color="info"
              size="x-small"
              label
            >
              배송중
            </VChip>
          </template>

          <template #bottom="{ pageCount }">
            <div class="pa-3">
              <VPagination
                v-model="deliveryPage"
                :length="pageCount"
                size="small"
                rounded
              />
            </div>
          </template>
        </VDataTable>
      </VCardText>
    </VCard>

    <!-- ══════════════════════════════════════════════ -->
    <!-- 배송 처리 다이얼로그                              -->
    <!-- ══════════════════════════════════════════════ -->
    <VDialog v-model="isDeliveryDialogOpen" max-width="860" persistent no-click-animation @click:outside="onDeliveryOutsideClick">
      <VCard title="배송 처리">
        <template #append>
          <VBtn icon variant="text" size="small" @click="isDeliveryDialogOpen = false">
            <VIcon icon="tabler-x" size="18" />
          </VBtn>
        </template>
        <VCardText>
          <VRow v-if="deliveryTarget">
            <!-- 왼쪽: 요청 정보 -->
            <VCol cols="12" md="5">
              <div class="mb-4">
                <p class="text-subtitle-2 font-weight-bold mb-3">요청 정보</p>
                <table class="info-table">
                  <tr>
                    <th>요청 구분</th>
                    <td>
                      <VChip :color="deliveryTarget.request_type === 'AUTO' ? 'info' : 'warning'" size="x-small" label>
                        {{ deliveryTarget.request_type_label }}
                      </VChip>
                    </td>
                  </tr>
                  <tr v-if="deliveryTarget.group_type">
                    <th>군 유형</th>
                    <td><strong>{{ deliveryTarget.group_type }}</strong></td>
                  </tr>
                  <tr>
                    <th>요청 사유</th>
                    <td>{{ deliveryTarget.reason_category }}</td>
                  </tr>
                  <tr>
                    <th>기관번호</th>
                    <td>{{ deliveryTarget.site_no }}</td>
                  </tr>
                  <tr>
                    <th>기관명</th>
                    <td>{{ deliveryTarget.site_name }}</td>
                  </tr>
                  <tr>
                    <th>요청량</th>
                    <td>{{ deliveryTarget.requested_quantity }}</td>
                  </tr>
                  <tr v-if="deliveryTarget.delivered_quantity > 0">
                    <th>기배송량</th>
                    <td>{{ deliveryTarget.delivered_quantity }}</td>
                  </tr>
                  <tr>
                    <th>잔여량</th>
                    <td>
                      <strong class="text-primary">{{ deliveryTarget.remaining_quantity ?? deliveryTarget.requested_quantity }}</strong>
                    </td>
                  </tr>
                  <tr>
                    <th>생성일</th>
                    <td>{{ formatDate(deliveryTarget.created_at) }}</td>
                  </tr>
                  <tr>
                    <th>희망도착일</th>
                    <td>{{ formatDate(deliveryTarget.desired_date) }}</td>
                  </tr>
                  <tr>
                    <th>기타 요청사항</th>
                    <td>{{ deliveryTarget.reason_detail || '-' }}</td>
                  </tr>
                </table>
              </div>
              <AppDateTimePicker
                v-model="deliveryForm.expected_arrival"
                label="배송 완료 예정일"
                placeholder="YYYY-MM-DD"
                class="mb-3"
              />
              <AppTextField
                v-model="deliveryForm.memo"
                label="비고"
                placeholder="비고 입력"
              />
            </VCol>

            <!-- 오른쪽: Drug No. 선택 -->
            <VCol cols="12" md="7">
              <div class="d-flex justify-space-between align-center mb-2">
                <p class="text-subtitle-2 font-weight-bold mb-0">전달할 Drug No. 선택</p>
                <VBtn
                  variant="outlined"
                  size="small"
                  prepend-icon="tabler-search"
                  :loading="searchingDrugs"
                  @click="searchDeliveryDrugs"
                >
                  검색
                </VBtn>
              </div>
              <p class="text-caption text-medium-emphasis mb-3">
                ⓘ 잔여 수량({{ deliveryTarget?.remaining_quantity ?? deliveryTarget?.requested_quantity }})에 맞는 Drug No.를 선택해주세요.
                검색 시 동일 군의 Drug No.가 최대 5개까지 조회됩니다.
              </p>

              <table v-if="selectedDrugs.length > 0" class="delivery-drug-table">
                <thead>
                  <tr>
                    <th>Drug No.</th>
                    <th style="width: 130px; text-align: center;">PTB 가용재고</th>
                    <th style="width: 120px">배송 수량</th>
                    <th style="width: 50px">관리</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(drug, idx) in selectedDrugs" :key="idx">
                    <td>{{ drug.drug_no }}</td>
                    <td class="text-center">{{ formatQty(drug.current_stock ?? 0) }}</td>
                    <td>
                      <AppTextField
                        v-model="drug.quantity"
                        type="number"
                        :step="decimalType === 'integer' ? 1 : decimalType === 'decimal1' ? 0.5 : 0.25"
                        density="compact"
                        hide-details
                        style="max-width: 80px"
                        @keydown="onDecimalInput"
                      />
                    </td>
                    <td class="text-center">
                      <VBtn icon variant="text" color="error" size="x-small" @click="removeDrug(idx)">
                        <VIcon icon="tabler-trash" size="16" />
                      </VBtn>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="mt-4 text-body-2">
                총 선택 수량: <strong>{{ formatQty(totalSelected) }}</strong> / 잔여 수량: <strong>{{ formatQty(deliveryTarget?.remaining_quantity ?? deliveryTarget?.requested_quantity ?? 0) }}</strong>
                <span v-if="deliveryTarget?.delivered_quantity > 0" class="text-caption text-medium-emphasis ms-2">
                  (전체 요청: {{ formatQty(deliveryTarget.requested_quantity) }}, 기배송: {{ formatQty(deliveryTarget.delivered_quantity) }})
                </span>
              </div>
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="justify-end pa-4 gap-2">
          <VBtn variant="outlined" @click="isDeliveryDialogOpen = false">취소</VBtn>
          <VBtn color="primary" @click="saveDelivery">배송 처리 저장</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- ══════════════════════════════════════════════ -->
    <!-- 일반 배송 처리 팝업 (배송 추가 / 수동배송 처리)       -->
    <!-- ══════════════════════════════════════════════ -->
    <VDialog v-model="isGeneralDeliveryOpen" max-width="680" persistent no-click-animation @click:outside="onGeneralDeliveryOutsideClick">
      <VCard title="배송 처리">
        <template #append>
          <VBtn icon variant="text" size="small" @click="isGeneralDeliveryOpen = false">
            <VIcon icon="tabler-x" size="18" />
          </VBtn>
        </template>
        <VCardText>
          <!-- ① 배송 정보 -->
          <div class="mb-1">
            <p class="text-subtitle-2 font-weight-bold mb-3">
              <VIcon icon="tabler-circle-1-filled" size="20" color="primary" class="me-1" />
              배송 정보
            </p>
            <VRow>
              <VCol cols="6">
                <AppSelect
                  v-model="generalForm.site"
                  :items="siteOptions"
                  label="배송 대상 Site No."
                  placeholder="Site No. 선택"
                  :disabled="!!generalDeliveryTarget"
                />
                <!-- 선택된 Site 정보 표시 -->
                <div v-if="generalForm.site" class="mt-2 pa-2" style="background: #f5f5f5; border-radius: 6px; font-size: 13px;">
                  <div><strong>Site No.</strong> {{ siteOptions.find(s => s.value === generalForm.site)?.site_no }}</div>
                  <div><strong>기관명</strong> {{ siteOptions.find(s => s.value === generalForm.site)?.site_name }}</div>
                </div>
              </VCol>
              <VCol cols="6">
                <AppDateTimePicker
                  v-model="generalForm.expected_arrival"
                  label="도착 예정일"
                  placeholder="YYYY-MM-DD"
                />
              </VCol>
            </VRow>
          </div>

          <VDivider class="my-4" />

          <!-- ② 배송 Drug No. 목록 -->
          <div>
            <p class="text-subtitle-2 font-weight-bold mb-3">
              <VIcon icon="tabler-circle-2-filled" size="20" color="primary" class="me-1" />
              배송 Drug No. 목록
            </p>

            <!-- Drug No. 검색 -->
            <div class="d-flex align-center gap-2 mb-4">
              <AppTextField
                v-model="generalDrugSearch"
                placeholder="Drug No."
                hide-details
                density="compact"
                style="max-inline-size: 300px;"
                @keyup.enter="searchDrugByNo"
              />
              <VBtn
                variant="outlined"
                size="small"
                :loading="generalSearching"
                @click="searchDrugByNo"
              >
                검색
              </VBtn>
            </div>

            <!-- Drug 테이블 -->
            <table v-if="generalDrugs.length > 0" class="delivery-drug-table">
              <thead>
                <tr>
                  <th>Drug No.</th>
                  <th style="width: 130px; text-align: center;">PTB 가용재고</th>
                  <th style="width: 160px; text-align: center;">배송수량</th>
                  <th style="width: 60px; text-align: center;">삭제</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(drug, idx) in generalDrugs" :key="drug.id">
                  <td>{{ drug.drug_no }}</td>
                  <td class="text-center">{{ formatQty(drug.current_stock) }}</td>
                  <td class="text-center">
                    <div class="d-flex align-center justify-center gap-1">
                      <VBtn icon variant="outlined" size="x-small" @click="adjustQty(idx, -1)">
                        <VIcon icon="tabler-minus" size="14" />
                      </VBtn>
                      <input
                        :value="formatQty(drug.quantity)"
                        type="text"
                        inputmode="decimal"
                        class="qty-direct-input"
                        @change="e => onDirectQtyInput(idx, e.target.value)"
                      >
                      <VBtn icon variant="outlined" size="x-small" @click="adjustQty(idx, 1)">
                        <VIcon icon="tabler-plus" size="14" />
                      </VBtn>
                    </div>
                  </td>
                  <td class="text-center">
                    <VBtn icon variant="text" color="error" size="x-small" @click="removeGeneralDrug(idx)">
                      <VIcon icon="tabler-trash" size="16" />
                    </VBtn>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-else class="text-caption text-medium-emphasis pa-4 text-center" style="border: 1px dashed #e0e0e0; border-radius: 8px;">
              Drug No.를 검색하여 추가해주세요.
            </div>

            <!-- 총 배송수량 -->
            <div v-if="generalDrugs.length > 0" class="mt-3 pa-2 d-inline-flex align-center gap-1" style="background: #e8f5e9; border-radius: 6px;">
              <strong>총 배송수량</strong>
              <VChip color="success" size="small" label>{{ formatQty(generalTotalQty) }}</VChip>
            </div>
          </div>
        </VCardText>
        <VCardActions class="justify-end pa-4 gap-2">
          <VBtn variant="outlined" @click="isGeneralDeliveryOpen = false">취소</VBtn>
          <VBtn color="primary" @click="saveGeneralDelivery">배송 처리</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- ══════════════════════════════════════════════ -->
    <!-- 배송 완료예정일 수정 다이얼로그                    -->
    <!-- ══════════════════════════════════════════════ -->
    <VDialog v-model="isEditArrivalOpen" max-width="360" persistent no-click-animation @click:outside="onEditArrivalOutsideClick">
      <VCard title="배송 완료예정일 수정">
        <template #append>
          <VBtn icon variant="text" size="small" @click="isEditArrivalOpen = false">
            <VIcon icon="tabler-x" size="18" />
          </VBtn>
        </template>
        <VCardText>
          <AppDateTimePicker
            v-model="editArrivalDate"
            label="배송 완료예정일"
            placeholder="YYYY-MM-DD"
          />
          <p class="text-caption text-medium-emphasis mt-2">
            ※ 예정일보다 빨리 도착한 경우 일자를 수정하여 조기 사용이 가능합니다.
          </p>
        </VCardText>
        <VCardActions class="justify-end pa-4 gap-2">
          <VBtn variant="outlined" @click="isEditArrivalOpen = false">취소</VBtn>
          <VBtn color="primary" @click="saveEditArrival">저장</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.rtms-table :deep(th),
.rtms-table :deep(td) {
  white-space: nowrap;
  font-size: 13px;
  padding: 6px 8px !important;
}

.rtms-table :deep(th) {
  font-size: 12px;
  font-weight: 600;
}

.rtms-table :deep(tr) {
  cursor: default;
}

.rtms-table :deep(td) {
  color: inherit;
  text-decoration: none;
}

.rtms-table :deep(td a) {
  color: inherit;
  text-decoration: none;
  pointer-events: none;
}

.edit-arrival-btn {
  position: absolute;
  right: -28px;
}

.delivery-drug-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  border: 1px solid #e0e0e0;
}

.delivery-drug-table th {
  text-align: left;
  font-weight: 600;
  font-size: 12px;
  color: #666;
  padding: 8px 10px;
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
}

.delivery-drug-table td {
  padding: 6px 10px;
  border: 1px solid #e0e0e0;
  vertical-align: middle;
}

.info-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  border: 1px solid #e0e0e0;
}

.info-table th {
  text-align: left;
  color: #666;
  padding: 8px 12px;
  width: 100px;
  white-space: nowrap;
  vertical-align: middle;
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  font-weight: 600;
}

.info-table td {
  padding: 8px 12px;
  vertical-align: middle;
  border: 1px solid #e0e0e0;
}

.qty-direct-input {
  width: 50px;
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 2px 4px;
  outline: none;
}

.qty-direct-input:focus {
  border-color: #3E71FE;
}
</style>
